import React from "react";
import IdleTimer from "react-idle-timer";

import {
  ServiceStatusEnum,
  SessionService,
  ISessionState,
  ISession,
  IHeartbeatState,
  IIdleState,
  IActiveState
} from "@imaginelearning/mps-api-service";
import { logout as logoutUser } from "helpers/auth-service";
import EdgeAlert from "../edge-alert/edge-alert";
import IdleModal from "../modals/idle/idle-modal";
import LoggedOutModal from "../modals/logged-out/logged-out-modal";
import SessionModal from "../modals/session/session-modal";
import AppWindow from "../../helpers/app-window";
import { postLogoffRedirect } from "../../helpers/user-session";

const appWindow = AppWindow() as any;
const idleActionTime = 1000 * 60 * Number(appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_ACTION_AFTER_MINUTES);
const idleEventTime = 1000 * 60 * Number(appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_BACKEND_EVENT_AFTER_MINUTES);
const idleModalTime = 1000 * 60 * Number(appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_MODAL_AFTER_MINUTES);
const sessionTimeout = 1000 * 60 * Number(appWindow.STUDENT_ENV.REACT_APP_SESSION_TIMEOUT_AFTER_MINUTES);
const logoutTime = 1000 * 60 * Number(appWindow.STUDENT_ENV.REACT_APP_LOGOUT_TIMEOUT_MINUTES);
const sessionIdKey = appWindow.STUDENT_ENV.REACT_APP_SESSION_KEYNAME as string;
const logoutMessageKey = appWindow.STUDENT_ENV.REACT_APP_LOGOUT_MESSAGE_KEYNAME as string;
const imlpAuth = appWindow.STUDENT_ENV.REACT_APP_IMLP_AUTHORITY as string;
const heartBeatTimeInterval = 60000;

enum SessionStateEnum {
  ActiveState,
  IdleActionState,
  IdleEventState,
  IdleModalState
}

const initialState = {
  pauseHeartbeat: true,
  forceSessionToken: "",
  showSessionModal: false,
  showLoggedOutModal: false,
  showIdleModal: false,
  isTimedOut: false,
  timeout: idleActionTime,
  sessionState: SessionStateEnum.ActiveState
};

const loggedOutState = {
  pauseHeartbeat: true,
  showLoggedOutModal: true,
  isTimedOut: true,
  timeout: logoutTime
};

interface IState {
  pauseHeartbeat: boolean;
  forceSessionToken: string;
  showSessionModal: boolean;
  showLoggedOutModal: boolean;
  showIdleModal: boolean;
  isTimedOut: boolean;
  timeout: number;
  sessionState: SessionStateEnum;
}

interface IProps {
  handleSessionSet: () => void;
  authTokenExpiry: number;
}

function isLegacyUser() {
  return window.location.hostname.toLowerCase().indexOf("edgenuity") >= 0;
}

class EdgeSessionManager extends React.Component<IProps, IState> {
  public heartbeatInterval: any;

  private idleTimer: any;

  constructor(props: IProps) {
    super(props);

    this.handleHeartBeat = this.handleHeartBeat.bind(this);
    this.handleIdle = this.handleIdle.bind(this);
    this.handleAction = this.handleAction.bind(this);
    this.getSessionId = this.getSessionId.bind(this);
    this.handleIdleModalResume = this.handleIdleModalResume.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleSessionEnded = this.handleSessionEnded.bind(this);
    this.startHeartbeatInterval = this.startHeartbeatInterval.bind(this);
    this.handleCancelLogin = this.handleCancelLogin.bind(this);
    this.refreshToken = this.refreshToken.bind(this);
    this.startRefreshTokenTimer = this.startRefreshTokenTimer.bind(this);

    this.state = { ...initialState };
  }

  componentDidMount() {
    const imlpToken = sessionStorage.getItem("imlpToken");
    const imlpRefreshToken = sessionStorage.getItem("imlpRefreshToken");

    if (sessionStorage.getItem(sessionIdKey)) {
      this.setState({ pauseHeartbeat: false });
      this.props.handleSessionSet();
    } else {
      this.getSessionId();
    }

    if (imlpToken && imlpRefreshToken) {
      const tokenPayload = JSON.parse(atob(imlpToken.split(".")[1]));
      const tokenExpiry = tokenPayload.exp;
      this.startRefreshTokenTimer(tokenExpiry);
    }
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (prevState.pauseHeartbeat !== this.state.pauseHeartbeat) {
      if (this.state.pauseHeartbeat) {
        clearInterval(this.heartbeatInterval);
      } else {
        this.startHeartbeatInterval();
      }
    }
  }

  // Refresh IMLP token
  async refreshToken() {
    try {
      const refreshToken = sessionStorage.getItem("imlpRefreshToken");
      const accessToken = sessionStorage.getItem("imlpToken");
      if (!refreshToken || !accessToken) return;

      // Extract the client_id from the current access token
      const tokenPayload = JSON.parse(atob(accessToken.split(".")[1]));
      const clientId = tokenPayload.client_id;

      const response = await fetch(`${imlpAuth}/connect/token`, {
        method: "POST",
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: clientId,
          scope: "offline_access openid"
        })
      });

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem("imlpToken", data.access_token);
        sessionStorage.setItem("imlpRefreshToken", data.refresh_token);

        // Extract the new token's expiration time and start the timer for the next refresh
        const newTokenPayload = JSON.parse(atob(data.access_token.split(".")[1]));
        const newTokenExpiry = newTokenPayload.exp;
        this.startRefreshTokenTimer(newTokenExpiry);
      } else {
        console.error("Failed to refresh token");
        this.logout("TokenRefreshFailure");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.logout("TokenRefreshError");
    }
  }

  startRefreshTokenTimer = (tokenExpiry: number) => {
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    const bufferTime = 120; // Buffer time in seconds (e.g., 2 minutes before expiration)
    const refreshTime = (tokenExpiry - currentTime - bufferTime) * 1000; // Convert to milliseconds

    if (refreshTime > 0) {
      setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    } else {
      console.warn("Token has already expired or is close to expiration. Logging out.");
      this.logout("TokenExpired");
    }
  };

  startHeartbeatInterval = () => {
    this.heartbeatInterval = setInterval(() => this.handleHeartBeat(), heartBeatTimeInterval);
  };

  handleIdle = () => {
    const { isTimedOut } = this.state;
    if (isTimedOut) {
      this.logout();
    } else {
      switch (this.state.sessionState) {
        case SessionStateEnum.ActiveState:
          this.setState(
            {
              timeout: idleEventTime - idleActionTime,
              sessionState: SessionStateEnum.IdleActionState
            },
            () => {
              this.idleTimer.reset();
            }
          );
          break;
        case SessionStateEnum.IdleActionState:
          this.setState(
            {
              timeout: idleModalTime - idleEventTime,
              sessionState: SessionStateEnum.IdleEventState
            },
            () => {
              this.idleTimer.reset();
            }
          );
          SessionService.createIdleEvent().then((response: IIdleState) => {
            if (response.status === ServiceStatusEnum.ERROR) {
              this.logout("default");
            }
          });
          break;
        case SessionStateEnum.IdleEventState:
          this.setState(
            {
              timeout: sessionTimeout - idleModalTime,
              sessionState: SessionStateEnum.IdleModalState,
              showIdleModal: true
            },
            () => {
              this.idleTimer.reset();
            }
          );
          break;
        case SessionStateEnum.IdleModalState:
          this.logout("IdleTimeout");
          break;
        default:
          break;
      }
    }
  };

  handleAction = () => {
    const { sessionState } = this.state;
    if (sessionState === SessionStateEnum.IdleActionState || sessionState === SessionStateEnum.IdleEventState) {
      if (sessionState === SessionStateEnum.IdleEventState) {
        SessionService.createActiveEvent().then((response: IActiveState) => {
          if (response.status === ServiceStatusEnum.ERROR) {
            this.setState({ ...loggedOutState }, () => clearInterval(this.heartbeatInterval));
          }
        });
      }

      this.setState({
        sessionState: SessionStateEnum.ActiveState,
        pauseHeartbeat: false,
        timeout: idleActionTime
      });
    }
  };

  handleIdleModalResume = () => {
    this.setState({
      showIdleModal: false,
      pauseHeartbeat: false,
      sessionState: SessionStateEnum.ActiveState,
      timeout: idleActionTime
    });
    SessionService.createActiveEvent().then((response: IActiveState) => {
      if (response.status === ServiceStatusEnum.ERROR) {
        this.setState({ ...loggedOutState }, () => clearInterval(this.heartbeatInterval));
      }
    });
  };

  handleSessionEnded = (errorMessage: string) => {
    let sessionEndedType = "default";
    if (errorMessage && errorMessage.indexOf && errorMessage.indexOf("Session ended") > -1) {
      const errorObj = JSON.parse(errorMessage);
      sessionEndedType = errorObj.errors.sessionId[0].replace("Session ended:", "").trim();
    }
    // handle case where heartbeattimeout occurred and simply create a new session to avoid odd logout behavior
    // we're dividing by 1000 to match the LTI tool division presumably to match .NET parsing rules?
    if (sessionEndedType === "HeartBeatTimeout" && this.props.authTokenExpiry > Date.now() / 1000) {
      this.getSessionId();
    } else {
      sessionStorage.setItem(logoutMessageKey, sessionEndedType);
      this.setState({ ...loggedOutState }, () => {
        this.idleTimer.reset();
        clearInterval(this.heartbeatInterval);
      });
    }
  };

  handleHeartBeat = () => {
    SessionService.createHeartbeat().then((response: IHeartbeatState) => {
      if (response.status === ServiceStatusEnum.ERROR) {
        this.handleSessionEnded(response.response);
      }
    });
  };

  handleLogout = () => {
    this.logout("UserLogoff");
  };

  handleTimerLogout = () => {
    this.logout("IdleTimeout");
  };

  handleCancelLogin = () => {
    clearInterval(this.heartbeatInterval);
    postLogoffRedirect();
  };

  logout = (sessionEndedType = "") => {
    clearInterval(this.heartbeatInterval);
    SessionService.logoff().then(() => {
      sessionStorage.removeItem(sessionIdKey);
      if (sessionEndedType) {
        sessionStorage.setItem(logoutMessageKey, sessionEndedType);
      }
      if (isLegacyUser()) {
        postLogoffRedirect();
      } else {
        logoutUser();
      }
    });
  };

  setSessionId = (sessionId: string) => {
    sessionStorage.setItem(sessionIdKey, sessionId);
  };

  getSessionId = async () => {
    try {
      const result: ISessionState<ISession> = await SessionService.createSession(this.state.forceSessionToken);
      if ((result.response as ISession).forceLoginToken && (result.response as ISession).sessionID !== "null") {
        this.setState({
          showSessionModal: false,
          forceSessionToken: (result.response as ISession).forceLoginToken,
          isTimedOut: true
        });
        await this.getSessionId();
        return;
      }

      if ((result.response as ISession).sessionID && (result.response as ISession).sessionID !== "null") {
        this.setSessionId((result.response as ISession).sessionID);
        this.setState({
          showSessionModal: false,
          pauseHeartbeat: false,
          isTimedOut: false
        });
        this.props.handleSessionSet();
      }

      if (result.status === ServiceStatusEnum.ERROR) {
        this.setState({ ...loggedOutState }, () => clearInterval(this.heartbeatInterval));
      }
    } catch (ex) {
      this.setState({ ...loggedOutState }, () => clearInterval(this.heartbeatInterval));
    }
  };

  getIdleTime = () => {
    switch (this.state.sessionState) {
      case SessionStateEnum.IdleActionState:
        return appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_ACTION_AFTER_MINUTES;
      case SessionStateEnum.IdleEventState:
        return appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_BACKEND_EVENT_AFTER_MINUTES;
      case SessionStateEnum.IdleModalState:
        return appWindow.STUDENT_ENV.REACT_APP_IDLE_SESSION_MODAL_AFTER_MINUTES;
      default:
        return null;
    }
  };

  render() {
    return (
      <>
        {process.env.NODE_ENV === "development" && this.state.sessionState !== SessionStateEnum.ActiveState && (
          <EdgeAlert body={`visible only in dev env: idle for ${this.getIdleTime()} minutes`} dismissible={true} />
        )}
        {this.state.showSessionModal && (
          <SessionModal
            showModal={this.state.showSessionModal}
            handleCancel={this.handleCancelLogin}
            handleSetSession={this.getSessionId}
          />
        )}
        {this.state.showLoggedOutModal && (
          <LoggedOutModal showModal={this.state.showLoggedOutModal} handleLogout={this.handleLogout} />
        )}
        {this.state.showIdleModal && (
          <IdleModal
            showModal={this.state.showIdleModal}
            handleLogout={this.handleTimerLogout}
            handleResume={this.handleIdleModalResume}
            timeout={this.state.timeout}
          />
        )}
        <IdleTimer
          ref={(ref: any) => {
            this.idleTimer = ref;
          }}
          element={document}
          onAction={this.handleAction}
          onIdle={this.handleIdle}
          debounce={250}
          timeout={this.state.timeout}
        />
      </>
    );
  }
}

export default EdgeSessionManager;
